/** A store sale is an overlay: the listing price is never changed. */
export const REFOCUS_STORE_UID = 'lGXm9tkerwOfJcjBYTFvzRvFqlZ2';
// D4 ruled (c) by Bill 2026-10-08 14:57 CDT: Refocus sales run 5-50% off and UPP
// absorbs the discount through the normal 93.5% payable (no top-up, no
// reconciliation change). This switch stays true, blocking Refocus sales, until
// UPP's written agreement is confirmed and the Roberts ledger shows UPP's ACH is
// computed on the sale price. The name keeps "D4" for grep continuity (it is
// referenced in 7 files); read it as "blocked pending UPP's written agreement".
// Keep this block in both the web and feed implementations until Bill says GO.
export const REFOCUS_STORE_SALE_BLOCKED_PENDING_D4 = true;

export interface StoreSaleProduct {
  price: number;
  seller_id: string;
  on_sale?: boolean | null;
  is_auction?: boolean | null;
}

export interface StoreSaleCurrent {
  sale_id: string;
  percent_off: number;
  starts_at: unknown;
  ends_at: unknown;
  cancelled_at?: unknown;
}

export interface StoreSaleConfig {
  enabled: boolean;
  allowlist?: string[] | null;
}

export interface AppliedStoreSale {
  saleId: string;
  percentOff: number;
  startsAt: number;
  endsAt: number;
}

export type EffectivePriceReason =
  | 'applied' | 'invalid_price' | 'refocus_pending_d4' | 'disabled'
  | 'not_allowlisted' | 'no_sale' | 'cancelled' | 'not_started'
  | 'ended' | 'auction' | 'own_markdown' | 'invalid_sale';

export interface EffectivePriceResult {
  unitCents: number;
  listCents: number;
  applied: AppliedStoreSale | null;
  reason: EffectivePriceReason;
}

/** A seller minimum never permits an offer above the price to buy now. */
export function getOfferPriceCeiling(buyNowPrice: number, _minimumOfferPrice: number): number {
  return buyNowPrice;
}

/** Accept Firestore Timestamp, epoch milliseconds, or ISO text at the API boundary. */
export function saleInstant(value: unknown): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return Date.parse(value);
  if (value && typeof value === 'object') {
    const timestamp = value as { toMillis?: () => number; seconds?: number; _seconds?: number };
    if (typeof timestamp.toMillis === 'function') return timestamp.toMillis();
    if (typeof timestamp.seconds === 'number') return timestamp.seconds * 1000;
    if (typeof timestamp._seconds === 'number') return timestamp._seconds * 1000;
  }
  return NaN;
}

/** Names only the invalid field, so server logs never need the sale payload. */
export function invalidStoreSaleField(sale: StoreSaleCurrent): string | null {
  if (!sale || typeof sale !== 'object') return 'sale';
  if (typeof sale.sale_id !== 'string' || !sale.sale_id) return 'sale_id';
  if (!Number.isInteger(sale.percent_off) || sale.percent_off < 5 || sale.percent_off > 50) return 'percent_off';
  const startsAt = saleInstant(sale.starts_at);
  const endsAt = saleInstant(sale.ends_at);
  if (!Number.isFinite(startsAt)) return 'starts_at';
  if (!Number.isFinite(endsAt)) return 'ends_at';
  if (endsAt <= startsAt || endsAt - startsAt < 60 * 60 * 1000 ||
      endsAt - startsAt > 30 * 24 * 60 * 60 * 1000) return 'duration';
  return null;
}

export function effectivePrice(
  product: StoreSaleProduct,
  sale: StoreSaleCurrent | null | undefined,
  nowMs: number,
  config: StoreSaleConfig
): EffectivePriceResult {
  const listCents = Math.round(product.price * 100);
  const noSale = (reason: EffectivePriceReason): EffectivePriceResult => ({
    unitCents: listCents, listCents, applied: null, reason,
  });
  if (!Number.isFinite(product.price) || product.price < 1) return noSale('invalid_price');
  if (REFOCUS_STORE_SALE_BLOCKED_PENDING_D4 && product.seller_id === REFOCUS_STORE_UID) {
    return noSale('refocus_pending_d4');
  }
  if (config.enabled !== true) return noSale('disabled');
  if (config.allowlist && !config.allowlist.includes(product.seller_id)) return noSale('not_allowlisted');
  if (!sale) return noSale('no_sale');
  if (sale.cancelled_at != null) return noSale('cancelled');

  if (invalidStoreSaleField(sale)) return noSale('invalid_sale');
  const startsAt = saleInstant(sale.starts_at);
  const endsAt = saleInstant(sale.ends_at);
  if (nowMs < startsAt) return noSale('not_started');
  if (nowMs >= endsAt) return noSale('ended');
  if (product.is_auction === true) return noSale('auction');
  if (product.on_sale === true) return noSale('own_markdown');

  const unitCents = Math.round(listCents * (100 - sale.percent_off) / 100);
  if (unitCents < 1 || unitCents >= listCents) return noSale('invalid_sale');
  return {
    unitCents, listCents, reason: 'applied',
    applied: { saleId: sale.sale_id, percentOff: sale.percent_off, startsAt, endsAt },
  };
}
